import React from "react";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import CardDropdown from "components/common/CardDropdown";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { Dropdown } from "react-bootstrap";
import TablePaginationFooter from "components/common/TablePaginationFooter";

export default function RoutesTable({
  routes,
  onEdit,
  setShowDeleteModal,
  setRouteToDelete,
  page,
  pageSize,
  totalPages,
  totalElements,
  setPage,
  setPageSize,
}) {
  const formatText = (text) => `${text.slice(0, 25)}...`;

  const confirmDelete = (id) => {
    setRouteToDelete(id);
    setShowDeleteModal(true);
  };

  const columns = [
    {
      accessorKey: "path",
      header: "Path",
      meta: {
        headerProps: { style: { width: '300px' }, className: "text-900 fw-bold" }, // Standard Bold Header
      },
      cell: ({ getValue }) => {
        const val = getValue() || "";
        return (
          <h6
            className="mb-0 text-900 fs-10 text-truncate d-inline-block fw-normal font-monospace" // Kept Bold
            style={{ maxWidth: 300, cursor: 'help' }}
            title={val}
          >
            {val}
          </h6>
        );
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      meta: {
        headerProps: { style: { width: '250px' }, className: "text-900 fw-bold" }, // Standard Bold Header
        cellProps: { className: "align-middle" },
      },
      cell: ({ getValue }) => {
        const text = getValue() || "No description provided";
        const displayText = text.length > 25 ? formatText(text) : text;
        return (
          <span
            className="text-500 d-inline-block fw-normal" // Standardized weight
            style={{ fontSize: 12, lineHeight: 1.4, cursor: 'help' }} // Standardized size
            title={text}
          >
            {displayText}
          </span>
        );
      },
    },
    {
      accessorKey: "active",
      header: "Status",
      meta: {
        headerProps: { style: { width: '100px' }, className: "text-900 text-center fw-bold" }, // Standard Bold Header
        cellProps: { className: "text-center" },
      },
      cell: ({ getValue }) => {
        const value = getValue();
        const style = {
          backgroundColor: value ? "#e8f7e3" : "#fbe9e7",
          color: value ? "#2e7d32" : "#c62828",
          padding: "2px 6px",
          borderRadius: "4px",
          fontSize: "0.7rem",
          lineHeight: 1.2,
          border: value ? "1px solid #d5ebd0" : "1px solid #f5c6cb",
          cursor: "default",
          fontWeight: 600, // Pill remains 600
          display: "inline-block",
          whiteSpace: "nowrap",
          verticalAlign: "middle",
          textTransform: "uppercase"
        };
        const content = value === true ? "ACTIVE" : "DISABLED";
        return <span style={style}>{content}</span>;
      },
    },
    {
      accessorKey: "priority",
      header: "Priority",
      meta: {
        headerProps: { style: { width: '80px' }, className: "text-900 text-center fw-bold" }, // Standard Bold Header
        cellProps: { className: "text-center fw-normal" },
      },
      cell: ({ getValue }) => <span style={{ fontSize: 12 }}>{getValue()}</span>
    },
    {
      accessorKey: "scopeRules",
      header: "Rules",
      meta: {
        headerProps: { style: { width: '80px' }, className: "text-900 text-center fw-bold" }, // Standard Bold Header
        cellProps: { className: "text-center" },
      },
      cell: ({ getValue }) => {
        const rules = getValue() || [];
        return (
          <span className="fw-bold text-700" style={{ fontSize: 12 }}>{rules.length}</span> // Kept Bold
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      meta: {
        headerProps: { style: { width: '50px' }, className: "text-900 text-center fw-bold" }, // Standard Bold Header
        cellProps: { className: "text-center" },
      },
      cell: ({ row }) => (
        <CardDropdown drop="start">
          <div className="py-2 fw-normal">
            <Dropdown.Item onClick={() => onEdit(row.original)}>
              View/edit
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => confirmDelete(row.original.id)}
              className="text-danger"
            >
              Delete
            </Dropdown.Item>
          </div>
        </CardDropdown>
      ),
    },
  ];

  const table = useAdvanceTable({
    data: routes || [],
    columns,
    sortable: true,
    pagination: true,
    perPage: pageSize,
  });

  return (
    <AdvanceTableProvider {...table}>
      <div className="mx-n1 px-1">
        <AdvanceTable
          headerClassName="bg-light text-nowrap align-middle text-dark"
          rowClassName="align-middle white-space-nowrap fs-10"
          tableProps={{
            bordered: true,
            striped: true,
            className: "mb-0 overflow-hidden",
          }}
        />
      </div>
      <div className="mt-3">
        <TablePaginationFooter
          page={page}
          totalPages={totalPages || 0}
          totalElements={totalElements || 0}
          onPrev={page > 0 && totalElements > 0 ? () => setPage(page - 1) : undefined}
          onNext={page < totalPages - 1 && totalElements > 0 ? () => setPage(page + 1) : undefined}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
        />
      </div>
    </AdvanceTableProvider>
  );
}